import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, XCircle, Phone, MessageCircle, FileText, User } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "../AdminLayout";
import { adminAxios, formatErr } from "../api";
import { useCatalog } from "../../contexts/CatalogContext";

export default function AdminApplicationDetail() {
  const { ref } = useParams();
  const navigate = useNavigate();
  const [app, setApp] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const { categories: CATEGORIES } = useCatalog();

  const load = () => {
    adminAxios.get(`/applications/${ref}`)
      .then((r) => setApp(r.data))
      .catch((e) => toast.error(formatErr(e)));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [ref]);

  const approve = async () => {
    if (!window.confirm("Approve this applicant and create a technician profile?")) return;
    setBusy(true);
    try {
      const { data } = await adminAxios.post(`/applications/${ref}/approve`);
      toast.success("Approved — technician created");
      navigate(`/admin/technicians/${data.technician.id}`);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    if (!reason.trim()) {
      toast.error("Please add a rejection reason");
      return;
    }
    setBusy(true);
    try {
      await adminAxios.post(`/applications/${ref}/reject`, { reason });
      toast.success("Application rejected");
      load();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  if (!app) return <AdminLayout title="Application"><div className="text-slate-500">Loading…</div></AdminLayout>;

  const isPending = app.status === "pending";

  return (
    <AdminLayout title={`Application ${app.ref_id}`}>
      <button onClick={() => navigate(-1)} className="mb-4 text-sm font-semibold text-slate-500 hover:text-[#1A3C6E] flex items-center gap-1" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</div>
                <div className="mt-1 text-lg font-display font-bold capitalize text-slate-900">{app.status}</div>
                {app.status === "rejected" && app.rejection_reason && (
                  <div className="mt-2 text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{app.rejection_reason}</div>
                )}
              </div>
              <div className="w-12 h-12 rounded-full bg-[#1A3C6E]/10 flex items-center justify-center text-[#1A3C6E]">
                <User className="w-6 h-6" />
              </div>
            </div>
          </div>

          <Section title="Personal">
            <Field k="Full name" v={app.full_name} />
            <Field k="Phone" v={app.phone} action={
              <div className="flex gap-2">
                <a href={`tel:${app.phone}`} className="text-xs font-semibold text-[#1A3C6E] hover:underline">Call</a>
                <a href={`https://wa.me/91${app.phone}`} target="_blank" rel="noreferrer" className="text-xs font-semibold text-[#22C55E] hover:underline">WhatsApp</a>
              </div>
            } />
            {app.email && <Field k="Email" v={app.email} />}
            <Field k="City" v={app.city} capitalize />
            <Field k="Experience" v={app.experience} />
          </Section>

          <Section title="Skills & Brands">
            <div className="flex flex-wrap gap-2 mb-3">
              {(app.services || []).map((sid) => {
                const c = CATEGORIES.find((c) => c.id === sid);
                return (
                  <span key={sid} className="px-3 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: c?.bg || "#E2E8F0", color: c?.color || "#475569" }}>
                    {c?.short || sid}
                  </span>
                );
              })}
            </div>
            <Field k="Brands" v={(app.brands || []).join(", ") || "—"} />
          </Section>

          <Section title="KYC & Bank">
            <Field k="Aadhar number" v={app.aadhar_number} />
            <Field k="Aadhar front" v={app.aadhar_front_name || "—"} icon={FileText} />
            <Field k="Aadhar back" v={app.aadhar_back_name || "—"} icon={FileText} />
            <Field k="Profile photo" v={app.profile_photo_name || "—"} icon={FileText} />
            <Field k="Certification" v={app.certification_name || "—"} icon={FileText} />
            <Field k="Bank account" v={app.bank_account || "—"} />
            <Field k="IFSC" v={app.ifsc || "—"} />
            <Field k="UPI ID" v={app.upi_id || "—"} />
            <p className="text-xs text-slate-400 mt-3">Document files are recorded by name only in MVP. Image preview will work once object storage is enabled.</p>
          </Section>
        </div>

        {/* Action panel */}
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-display font-bold text-sm uppercase tracking-wider text-slate-500 mb-3">Decision</h3>
            {!isPending ? (
              <p className="text-sm text-slate-500">
                {app.status === "approved" ? "Already approved." : "Already rejected."}
              </p>
            ) : (
              <>
                <button
                  onClick={approve}
                  disabled={busy}
                  data-testid="approve-btn"
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#22C55E] hover:bg-[#16A34A] text-white font-semibold py-2.5 rounded-lg disabled:opacity-60"
                >
                  <CheckCircle2 className="w-4 h-4" /> Approve & create technician
                </button>
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <label className="text-xs font-semibold text-slate-600">Rejection reason</label>
                  <textarea
                    rows={3}
                    data-testid="reject-reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="mt-1 w-full px-3 py-2 text-sm rounded-lg border border-slate-200 outline-none focus:border-[#EF4444]"
                    placeholder="E.g. Skills don't match service area…"
                  />
                  <button
                    onClick={reject}
                    disabled={busy}
                    data-testid="reject-btn"
                    className="mt-2 w-full inline-flex items-center justify-center gap-2 bg-[#EF4444] hover:bg-[#DC2626] text-white font-semibold py-2.5 rounded-lg disabled:opacity-60"
                  >
                    <XCircle className="w-4 h-4" /> Reject
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

const Section = ({ title, children }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5">
    <h3 className="font-display font-bold text-sm uppercase tracking-wider text-slate-500 mb-3">{title}</h3>
    {children}
  </div>
);

const Field = ({ k, v, action, capitalize, icon: Icon }) => (
  <div className="flex items-start justify-between gap-3 py-1.5">
    <div className="flex items-start gap-2 min-w-0">
      {Icon && <Icon className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />}
      <div className="min-w-0">
        <div className="text-xs text-slate-500">{k}</div>
        <div className={`text-sm font-semibold text-slate-900 break-words ${capitalize ? "capitalize" : ""}`}>{v || "—"}</div>
      </div>
    </div>
    {action}
  </div>
);
